/**
 * Copy-to-Office utilities for assistant message bubbles.
 *
 * Both Word and Excel paste-from-clipboard handle `text/html` content
 * natively — Word renders headings/lists/tables; Excel parses the FIRST
 * `<table>` it finds. So the simplest cross-tool affordance is to
 * publish the rendered HTML alongside a plain-text fallback via the
 * Clipboard API's `ClipboardItem` with two MIME types. No external
 * library needed.
 *
 * Per the IndsigtFrontend port + the 2026 chat-UI research:
 *   - copyAsWord   → HTML + plain text on the clipboard (paste-in-Word)
 *   - copyAsExcel  → text/html clipboard (Excel picks up the first table)
 *                    + a TSV plain-text fallback for `paste-special as text`
 *                    when no table exists in the answer.
 *
 * The Vue side calls these with the already-rendered DOM (passed in as
 * an HTMLElement) so we get exactly what the user sees, [N] citation
 * chips and all. We strip the cite buttons before publishing so they
 * don't paste as awkward "[N]" tokens in Word.
 */

/** Strip Choir-specific cite button markup from a cloned DOM so the
 * clipboard content reads cleanly in Word. We replace each cite with
 * a plain `[N]` superscript-style span so the citation index is still
 * visible — typical Word user wants the citation marker preserved. */
function cleanForOffice(root: HTMLElement): HTMLElement {
  const clone = root.cloneNode(true) as HTMLElement;
  const cites = clone.querySelectorAll<HTMLElement>(".bubble__cite, button.bubble__cite");
  cites.forEach((btn) => {
    const n = btn.getAttribute("data-cite-n");
    const span = document.createElement("sup");
    span.textContent = n ? `[${n}]` : btn.textContent || "";
    btn.replaceWith(span);
  });
  // Tool round-trip cards + retrieval blocks aren't useful in Word —
  // they're UI affordances, not content. Drop them.
  clone.querySelectorAll(".bubble__tools, .bubble__retrieval, .bubble__workflow, .bubble__copy, .bubble__office-actions").forEach((el) => {
    el.remove();
  });
  return clone;
}

/** Convert a `<table>` to a TSV string (excel paste-friendly). Uses
 * `\t` between cells, `\n` between rows. Cells are run through
 * `textContent` so embedded HTML formatting is stripped — Excel cells
 * are plain text anyway. */
function tableToTsv(table: HTMLTableElement): string {
  const rows: string[] = [];
  table.querySelectorAll("tr").forEach((tr) => {
    const cells: string[] = [];
    tr.querySelectorAll<HTMLTableCellElement>("th, td").forEach((cell) => {
      // Excel TSV escape: tab + newline + return get collapsed.
      const txt = (cell.textContent ?? "").replace(/\s+/g, " ").trim();
      cells.push(txt);
    });
    if (cells.length) {
      rows.push(cells.join("\t"));
    }
  });
  return rows.join("\n");
}

/** Build an HTML fragment ready for Word paste. Wraps the cleaned
 * content in a minimal `<html><body>` shell so Word recognises it as
 * a complete fragment, not just a snippet. */
function buildWordHtml(cleaned: HTMLElement): string {
  const inner = cleaned.innerHTML.trim();
  return (
    `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body>`
    + inner
    + `</body></html>`
  );
}

/** Walk the cleaned DOM and produce a plain-text rendering with
 * basic structure (headings get blank-line separators, list items
 * get `- ` prefixes, table cells get tab-separated rows). Word's
 * plain-text fallback also reads OK in any text editor. */
function buildPlainText(cleaned: HTMLElement): string {
  const lines: string[] = [];
  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = (node.textContent ?? "").replace(/\s+/g, " ");
      if (t.trim()) lines.push(t);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    if (tag === "table") {
      lines.push("");
      lines.push(tableToTsv(el as HTMLTableElement));
      lines.push("");
      return;
    }
    if (tag === "li") {
      lines.push(`- ${(el.textContent ?? "").trim()}`);
      return;
    }
    if (/^h[1-6]$/.test(tag)) {
      lines.push("");
      lines.push((el.textContent ?? "").trim());
      lines.push("");
      return;
    }
    if (tag === "br") {
      lines.push("");
      return;
    }
    el.childNodes.forEach(walk);
    if (tag === "p" || tag === "div") {
      lines.push("");
    }
  };
  cleaned.childNodes.forEach(walk);
  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/** Publish HTML + plain-text variants to the clipboard. Word picks up
 * `text/html`; everything else falls back to `text/plain`. Returns
 * `true` on success, `false` on permission denial or unsupported API. */
async function writeClipboardItem(
  html: string,
  plain: string,
): Promise<boolean> {
  if (
    typeof navigator === "undefined"
    || !navigator.clipboard
    || typeof ClipboardItem === "undefined"
  ) {
    return false;
  }
  try {
    const item = new ClipboardItem({
      "text/html":  new Blob([html],  { type: "text/html"  }),
      "text/plain": new Blob([plain], { type: "text/plain" }),
    });
    await navigator.clipboard.write([item]);
    return true;
  } catch {
    return false;
  }
}

/** Copy the answer as Word-pasteable HTML + plain-text fallback. */
export async function copyAsWord(rendered: HTMLElement): Promise<boolean> {
  const cleaned = cleanForOffice(rendered);
  const html = buildWordHtml(cleaned);
  const plain = buildPlainText(cleaned);
  return writeClipboardItem(html, plain);
}

/** Copy the answer in Excel-pasteable form. When the answer contains
 * a `<table>`, the first one wins (Excel parses the HTML table directly
 * on paste). When there's no table, the plain-text fallback is a TSV
 * built from whatever list-shaped content was found, so `paste-special
 * → text` still produces something useful. */
export async function copyAsExcel(rendered: HTMLElement): Promise<boolean> {
  const cleaned = cleanForOffice(rendered);
  const tables = cleaned.querySelectorAll<HTMLTableElement>("table");
  let html: string;
  let plain: string;
  if (tables.length > 0) {
    const firstTable = tables[0];
    html = buildWordHtml(firstTable);
    plain = tableToTsv(firstTable);
  } else {
    // No table — build a single-column TSV from list items / paragraphs.
    html = buildWordHtml(cleaned);
    plain = buildPlainText(cleaned);
  }
  return writeClipboardItem(html, plain);
}

/** Whether the rendered answer has at least one `<table>` — used to
 * decide whether to show the "Copy for Excel" button. */
export function hasTable(rendered: HTMLElement): boolean {
  return rendered.querySelectorAll("table").length > 0;
}
